// @ts-nocheck
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Bot, Pause, Play, Plus, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { c, Section, Field, Message, Switch, IconBtn, useStudio, useNode } from '../ui';
import { newLayaAnswer, overlappingAnswers, toSnakeCase, validateLaya } from '../../layaLlm';

const EXAMPLE = {
  description: 'asks about check-in or check-out time',
  answer: 'Check-in is from 2 PM and check-out is until 11 AM.',
};

export default function LayaSection({ next, onNext, value, onChange, globallyDisabled }) {
  const { readOnly, touch } = useStudio();
  const node = useNode('laya');
  const [technical, setTechnical] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const { errors, warnings } = validateLaya(value);
  const overlaps = overlappingAnswers(value.answers);

  const patch = (next_) => onChange({ ...value, ...next_ });
  const patchAnswer = (id, next_) => onChange({ ...value, answers: value.answers.map((a) => (a.id === id ? { ...a, ...next_ } : a)) });
  const addAnswer = (seed = {}) => {
    touch('laya');
    onChange({ ...value, enabled: true, answers: [...value.answers, { ...newLayaAnswer(), ...seed }] });
  };

  
  const completeIds = useRef(null);
  const timers = useRef({});
  const pending = useRef(new Set());
  useEffect(() => {
    const now = new Set(value.answers.filter((a) => a.description?.trim() && a.answer?.trim()).map((a) => a.id));
    const before = completeIds.current;
    completeIds.current = now;
    if (before === null || readOnly) return;
    value.answers.forEach((a) => {
      const was = before.has(a.id);
      const is = now.has(a.id);
      if (is && (!was || pending.current.has(a.id))) {
        pending.current.add(a.id);
        clearTimeout(timers.current[a.id]);
        timers.current[a.id] = setTimeout(() => {
          pending.current.delete(a.id);
          toast.success('Fast answer added', { id: `laya-added-${a.id}` });
        }, 1500);
      } else if (!is) {
        pending.current.delete(a.id);
        clearTimeout(timers.current[a.id]);
      }
    });
  }, [value.answers, readOnly]);
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  const tag = node.bad ? 'attention' : value.enabled ? 'on' : value.answers.length ? 'paused' : 'optional';

  return (
    <Section
      id="laya"
      icon={Sparkles}
      title="Fast answers"
      sub="Ready answers for common questions. Matching questions skip the AI, so replies are instant."
      tag={tag}
      ok={node.ok}
      bad={node.bad}
      next={next('laya')}
      onNext={onNext}
    >
      {globallyDisabled && (
        <p className={c('note')} style={{ marginBottom: 12 }}>
          Fast answers are turned off for all agents by your admin. Your settings here are saved and will apply once it is switched back on.
        </p>
      )}

      <div className={c('tog')}>
        <Switch
          checked={!!value.enabled}
          label="Fast answers"
          onChange={(enabled) => {
            patch({ enabled });
            touch('laya');
            toast.success(enabled ? 'Fast answers turned on' : 'Fast answers paused');
          }}
        />
        <b>{value.enabled ? 'Fast answers are on' : value.answers.length ? 'Paused. Your answers are kept' : 'Fast answers are off'}</b>
        {!value.enabled && value.answers.length > 0 && (
          <button type="button" className={c('btn small')} onClick={() => patch({ enabled: true })}>
            <Play className="ags-i" /> Resume
          </button>
        )}
      </div>

      {!value.enabled && value.answers.length === 0 && (
        <p className={c('note')} style={{ margin: 0 }}>
          Add common questions with ready answers. Matching questions are answered instantly, without the AI.
        </p>
      )}

      {(value.enabled || value.answers.length > 0) && (
        <div style={value.enabled ? undefined : { opacity: 0.55, pointerEvents: 'none' }}>
          {errors.map((e) => (
            <Message key={e} error={e} />
          ))}
          {warnings.map((w) => (
            <p key={w} className={c('note')} style={{ margin: '6px 0' }}>
              {w}
            </p>
          ))}

          {value.answers.length === 0 ? (
            <div className={c('empty')}>
              <span className={c('ei')}>
                <Sparkles className="ags-i" />
              </span>
              <div>
                <b>No fast answers yet</b>
                <small>Pick the questions callers ask every day and write the reply once. Everything else still goes to the AI.</small>
                <div className={c('addrow')} style={{ marginTop: 10 }}>
                  <button type="button" className={c('btn small primary')} onClick={() => addAnswer()}>
                    <Plus className="ags-i" /> Add answer
                  </button>
                  <button type="button" className={c('btn small')} onClick={() => addAnswer(EXAMPLE)}>
                    <Sparkles className="ags-i" /> Insert an example
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {value.answers.map((a, i) => {
                const hasHint = /[<>]/.test(a.answer || '') || (a.description?.trim() && !a.answer?.trim());
                const descBad = a.answer?.trim() && !a.description?.trim();
                return (
                  <div className={c('form-card')} key={a.id || i} style={a.enabled === false ? { opacity: 0.6 } : undefined}>
                    <div className={c('fh')}>
                      <Sparkles className="ags-i" /> Answer {i + 1}
                      {a.enabled === false && <span className={c('st paused')} style={{ marginLeft: 8, fontSize: 11.5, padding: '2px 9px' }}>Paused</span>}
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        <IconBtn
                          icon={a.enabled === false ? Play : Pause}
                          label={a.enabled === false ? 'Resume this answer' : 'Pause this answer'}
                          onClick={() => {
                            patchAnswer(a.id, { enabled: a.enabled === false });
                            toast.success(a.enabled === false ? 'Answer resumed' : 'Answer paused');
                          }}
                        />
                        <IconBtn
                          icon={Trash2}
                          danger
                          label="Delete this answer"
                          onClick={() => {
                            onChange({ ...value, answers: value.answers.filter((x) => x.id !== a.id) });
                            toast.success('Fast answer removed');
                          }}
                        />
                      </span>
                    </div>
                    <Field label="When the caller asks" error={descBad && 'Say what the caller asks, so this answer can be found.'}>
                      <input
                        className={c('ctl', descBad && 'invalid')}
                        value={a.description}
                        placeholder="asks about parking"
                        onChange={(e) =>
                          patchAnswer(a.id, {
                            description: e.target.value,
                            label: a.labelEdited ? a.label : toSnakeCase(e.target.value),
                          })
                        }
                      />
                      {technical && (
                        <div className={c('note')} style={{ marginTop: 6 }}>
                          Label:{' '}
                          <input
                            className={c('ctl mono')}
                            style={{ minHeight: 34, padding: '4px 10px', width: 'min(100%,320px)' }}
                            value={a.label}
                            aria-label="Technical name"
                            onChange={(e) => patchAnswer(a.id, { label: e.target.value, labelEdited: true })}
                          />
                        </div>
                      )}
                    </Field>
                    <Field
                      label="Say this"
                      style={{ margin: 0 }}
                      count={(a.answer || '').length}
                      max={240}
                      note={hasHint ? '' : 'Keep it short. It is read aloud.'}
                      error={
                        /[<>]/.test(a.answer || '')
                          ? 'Remove < and >. Callers would hear placeholders read aloud.'
                          : a.description?.trim() && !a.answer?.trim()
                            ? 'Write what the agent should say back.'
                            : ''
                      }
                    >
                      <textarea
                        className={c('ctl', hasHint && 'invalid')}
                        rows={2}
                        value={a.answer}
                        placeholder="Parking is free for guests, in the basement."
                        onChange={(e) => patchAnswer(a.id, { answer: e.target.value })}
                      />
                    </Field>
                    {overlaps[a.id] && (
                      <div className={c('msg show err')} style={{ color: 'var(--amber)' }}>
                        <AlertTriangle className="ags-i" />
                        <span>
                          This sounds like "{overlaps[a.id]}". Callers may get the wrong reply. Make the two lines clearly different, or merge them into one answer.
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

              <div className={c('form-card')} style={{ opacity: 0.9 }}>
                <div className={c('fh')}>
                  <Bot className="ags-i" /> Everything else <span className={c('st opt')} style={{ marginLeft: 8, fontSize: 11.5, padding: '2px 9px' }}>Fixed</span>
                </div>
                <div className={c('note')}>Questions that match none of the answers above go to the AI. This row is always there.</div>
              </div>

              <div className={c('addrow')}>
                <button type="button" className={c('btn small')} onClick={() => addAnswer()}>
                  <Plus className="ags-i" /> Add a fast answer
                </button>
                {/* <button type="button" className={c('btn ghost')} onClick={() => setTechnical((v) => !v)}>
                  {technical ? 'Hide' : 'Show'} technical names
                </button>
                <button type="button" className={c('btn ghost')} onClick={() => setAdvanced((v) => !v)}>
                  {advanced ? 'Hide' : 'Show'} advanced
                </button> */}
              </div>

              {advanced && (
                <div className={c('form-card')} style={{ marginTop: 12 }}>
                  <div className={c('row3')}>
                    <Field label="Mode" style={{ margin: 0 }}>
                      <input className={c('ctl')} value={value.mode} onChange={(e) => patch({ mode: e.target.value })} />
                    </Field>
                    <Field label="Context turns" style={{ margin: 0 }}>
                      <input className={c('ctl')} type="number" min={0} value={value.contextTurns} onChange={(e) => patch({ contextTurns: Number(e.target.value) })} />
                    </Field>
                    <Field label="Language" style={{ margin: 0 }}>
                      <input className={c('ctl')} value={value.lang} onChange={(e) => patch({ lang: e.target.value })} />
                    </Field>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Section>
  );
}
